import { addDays, addWeeks, format, getISOWeek, startOfISOWeek } from "date-fns";
import { fr } from "date-fns/locale";

/**
 * Dates des maquettes du site public, calculées à partir d'aujourd'hui pour que la démo
 * ne date jamais (elle affichait « mars 2025 »). Semaine en cours = offset 0, S+1 = offset 1.
 */

const SHORT_DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const shortMonth = (day: Date) => format(day, "MMM", { locale: fr });
/** « 1er », « 2 », « 3 »… comme on écrit les dates en français. */
const dayOfMonth = (day: Date) => (day.getDate() === 1 ? "1er" : String(day.getDate()));
const weekdayIndex = (day: Date) => (day.getDay() + 6) % 7;

export interface DemoWeek {
  /** Numéro de semaine ISO, comme dans l'appli (« Sem. 45 »). */
  number: number;
  /** Du lundi au dimanche. */
  days: Date[];
  /** « 2–8 nov. » dans un même mois, « 26 oct. – 1er nov. » à cheval sur deux mois. */
  label: string;
}

const rangeLabel = (start: Date, end: Date) =>
  start.getMonth() === end.getMonth()
    ? `${dayOfMonth(start)}–${dayOfMonth(end)} ${shortMonth(end)}`
    : `${dayOfMonth(start)} ${shortMonth(start)} – ${dayOfMonth(end)} ${shortMonth(end)}`;

/** Semaine ISO décalée de `offset` semaines ; `lastDay` borne le libellé (6 = dimanche, 5 = samedi). */
export function demoWeek(offset = 0, today = new Date(), lastDay = 6): DemoWeek {
  const monday = startOfISOWeek(addWeeks(today, offset));
  const days = Array.from({ length: 7 }, (_, i) => addDays(monday, i));
  return { number: getISOWeek(monday), days, label: rangeLabel(days[0], days[lastDay]) };
}

/** « Lun 2 » : en-tête de colonne de la grille de planning. */
export const demoColumnLabel = (day: Date) => `${SHORT_DAYS[weekdayIndex(day)]} ${day.getDate()}`;

/** « Lun. 2 nov. » */
export const demoDayLabel = (day: Date) => `${SHORT_DAYS[weekdayIndex(day)]}. ${dayOfMonth(day)} ${shortMonth(day)}`;

/** « octobre », ou « Octobre » en début de libellé. */
export function demoMonthName({ capitalized = false } = {}, today = new Date()) {
  const name = format(today, "LLLL", { locale: fr });
  return capitalized ? name.charAt(0).toUpperCase() + name.slice(1) : name;
}

/** « octobre 2026 », ou « Octobre 2026 » en début de libellé. */
export const demoMonthYear = ({ capitalized = false } = {}, today = new Date()) =>
  `${demoMonthName({ capitalized }, today)} ${today.getFullYear()}`;

/** « de mars », « d'octobre » : élision devant avril, août et octobre. */
export const ofMonth = (monthName: string) => (/^[aeiouyàâéèêëîïôûù]/i.test(monthName) ? `d'${monthName}` : `de ${monthName}`);

/** « Du mardi 10 au jeudi 12 novembre 2026 » (mois et année répétés seulement s'ils changent). */
export function demoLongRange(start: Date, end: Date) {
  const weekday = (day: Date) => format(day, "EEEE", { locale: fr });
  const month = (day: Date) => format(day, "MMMM", { locale: fr });
  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();
  const startTail = sameMonth ? "" : sameYear ? ` ${month(start)}` : ` ${month(start)} ${start.getFullYear()}`;
  return `Du ${weekday(start)} ${dayOfMonth(start)}${startTail} au ${weekday(end)} ${dayOfMonth(end)} ${month(end)} ${end.getFullYear()}`;
}
